import { useCallback, useRef } from 'react';
import useForecastDashboard from './useForecastDashboard';
import ForecastCardStrip, { ForecastScrollButtons } from './ForecastCardStrip';
import ForecastCharts from './ForecastCharts';
import { FORECAST_INTERVAL_TABS } from './forecastIntervals';

export default function ForecastDashboard() {
  const cardsScrollRef = useRef(null);
  const {
    location,
    place,
    points,
    loading,
    error,
    selectedInterval,
    setSelectedInterval,
    refetch,
  } = useForecastDashboard();

  const locationLabel = [place?.name, place?.state, place?.country]
    .filter(Boolean)
    .join(', ') ||
    [location?.name, location?.city, location?.state]
      .filter(Boolean)
      .join(', ') ||
    'Your location';

  const handleCardStripScroll = useCallback((dir) => {
    const el = cardsScrollRef.current;
    if (!el) return;

    const firstCard = el.firstElementChild;
    const cardWidth = firstCard?.getBoundingClientRect().width ?? 124;
    const gap = parseFloat(window.getComputedStyle(el).columnGap || '0') || 0;
    const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const scrollAmount = cardWidth + gap;
    const nextLeft = Math.max(
      0,
      Math.min(maxLeft, el.scrollLeft + dir * scrollAmount)
    );

    el.scrollLeft = nextLeft;
  }, []);

  return (
    <div className="px-3 py-4 sm:px-5 sm:py-6">
      <div className="glass mx-auto max-w-[1400px] rounded-[20px] p-4 sm:p-6 lg:p-8">
        <div className="mb-1 flex flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
            Forecast
          </h1>
          {locationLabel && (
            <p className="text-xs text-gray-400 sm:text-sm">{locationLabel}</p>
          )}
        </div>

        <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="w-full min-w-0 max-w-full">
            <div className="grid w-full grid-cols-2 gap-1.5 rounded-[12px] border border-white/15 bg-white/[0.04] p-1.5 shadow-inner shadow-black/10 sm:flex sm:w-fit sm:flex-wrap">
              {FORECAST_INTERVAL_TABS.map((tab) => {
                const active = selectedInterval === tab.id;
                const [primaryLabel, secondaryLabel] = tab.label
                  .split('/')
                  .map((part) => part.trim());
                return (
                  <button
                    key={tab.id}
                    type="button"
                    disabled={loading}
                    onClick={() => setSelectedInterval(tab.id)}
                    className={`flex h-11 min-w-0 flex-col items-center justify-center rounded-[9px] px-2 text-center text-[11px] font-medium leading-tight transition sm:h-[52px] sm:min-w-[86px] sm:px-3 sm:text-[13px] ${
                      active
                        ? 'bg-primaryBlue text-white shadow-md shadow-primaryBlue/30'
                        : 'text-gray-300 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <span>{primaryLabel}</span>
                    <span>{secondaryLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex w-full justify-center lg:w-auto lg:shrink-0 lg:justify-end">
            <ForecastScrollButtons onScroll={handleCardStripScroll} />
          </div>
        </div>

        {error && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/15"
            >
              Retry
            </button>
          </div>
        )}

        <div className="mt-5 min-w-0 max-w-full overflow-hidden">
          <ForecastCardStrip
            points={points}
            loading={loading}
            containerRef={cardsScrollRef}
          />
        </div>

        <div className="mt-6">
          <ForecastCharts points={points} loading={loading} />
        </div>
      </div>
    </div>
  );
}
